/**
 * Generates a Google Static Maps API URL for displaying a map image with a marker.
 *
 * @param address - The address or location to display on the map
 * @param apiKey - The Google Maps API key
 * @param options - Optional configuration
 * @param options.zoomLevel - Zoom level (default: 12)
 * @param options.width - Image width in pixels (default: 518)
 * @param options.height - Image height in pixels (default: 321)
 * @param options.scale - Scale for retina displays (default: 2)
 * @param options.showMarker - Whether to show the location marker (default: true)
 * @returns A URL string for the static map image, or null if the API key is invalid
 *
 * @example
 * const mapUrl = getGoogleStaticMapForBuildHub('123 Main St, Auckland', 'YOUR_API_KEY');
 * // Returns: 'https://maps.googleapis.com/maps/api/staticmap?zoom=12&size=518x321&...'
 *
 * @example
 * const mapUrl = getGoogleStaticMapForBuildHub('Auckland', 'invalid-key');
 * // Returns: null (because API key is invalid)
 */
export const getGoogleStaticMapForBuildHub = (
    address: string,
    apiKey: string,
    options?: {
        zoomLevel?: number;
        width?: number;
        height?: number;
        scale?: number;
        showMarker?: boolean;
        markerSize?: 'tiny' | 'small' | 'mid';
    },
): string | null => {
    // Validate API key - only check for empty/whitespace
    // (allow placeholder to pass through as it might still work in some contexts)
    if (!apiKey || apiKey.trim() === '') {
        return null;
    }

    // Validate address
    if (!address || address.trim() === '') {
        return null;
    }

    // Default values
    const zoomLevel = options?.zoomLevel ?? 12;
    const width = options?.width ?? 518;
    const height = options?.height ?? 321;
    const scale = options?.scale ?? 2;
    const showMarker = options?.showMarker ?? true;
    const markerSize = options?.markerSize ?? 'mid';
    // Build the URL with URLSearchParams for proper encoding
    const params = new URLSearchParams();

    // Basic parameters
    params.append('zoom', zoomLevel.toString());
    params.append('size', `${width}x${height}`);
    params.append('scale', scale.toString());
    params.append('maptype', 'roadmap');

    // Premium modern styling
    // Water - elegant deep blue
    params.append('style', 'feature:water|element:geometry|color:0x2c3e50');
    params.append(
        'style',
        'feature:water|element:labels.text.fill|color:0x9ca5b3',
    );

    // Landscape - soft neutral with subtle warmth
    params.append('style', 'feature:landscape|element:geometry|color:0xf5f5f5');
    params.append(
        'style',
        'feature:landscape.natural|element:geometry|color:0xe8ebe8',
    );

    // Roads - clean hierarchy
    params.append('style', 'feature:road|element:geometry|color:0xffffff');
    params.append(
        'style',
        'feature:road.highway|element:geometry|color:0xffd966',
    );
    params.append(
        'style',
        'feature:road.highway|element:geometry.stroke|color:0xf5c842',
    );
    params.append(
        'style',
        'feature:road.arterial|element:geometry|color:0xffffff',
    );
    params.append(
        'style',
        'feature:road.arterial|element:geometry.stroke|color:0xe0e0e0',
    );
    params.append(
        'style',
        'feature:road.local|element:geometry|color:0xffffff',
    );

    // POI - minimal and refined
    params.append('style', 'feature:poi|element:geometry|color:0xe8ebe8');
    params.append('style', 'feature:poi.park|element:geometry|color:0xc8d5b9');
    params.append('style', 'feature:poi|element:labels.text|visibility:off');
    params.append('style', 'feature:poi.business|visibility:off');

    // Transit - subtle presence
    params.append('style', 'feature:transit|element:geometry|color:0xe0e0e0');
    params.append('style', 'feature:transit|element:labels|visibility:off');

    // Administrative - minimal borders
    params.append(
        'style',
        'feature:administrative|element:geometry.stroke|color:0xd0d0d0',
    );
    params.append(
        'style',
        'feature:administrative|element:labels.text.fill|color:0x7c7c7c',
    );

    // Buildings - subtle 3D feel
    params.append(
        'style',
        'feature:landscape.man_made|element:geometry|color:0xf0f0f0',
    );

    // Labels - clean and readable
    params.append(
        'style',
        'feature:road|element:labels.text.fill|color:0x4a4a4a',
    );
    params.append(
        'style',
        'feature:road|element:labels.text.stroke|color:0xffffff',
    );

    // Add premium marker with ArchiPro brand color if requested
    if (showMarker) {
        // Use a larger marker with ArchiPro brand color for premium feel
        // size:mid provides a more substantial, prominent marker
        params.append(
            'markers',
            `size:${markerSize}|color:0xB89369|${address}`,
        );
    } else {
        // Center the map on the address even without a marker
        params.append('center', address);
    }

    params.append('key', apiKey);

    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
};
